import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';
import AdmissionBg from "../../assets/img/website-images/admission-pharmacy.jpg"

/* ─── Variants ─────────────────────────────────────────────────── */
const fadeInLeft = {
    hidden: { opacity: 0, x: -70 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
    },
};

const fadeInRight = {
    hidden: { opacity: 0, x: 70 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
    },
};

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (delay = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.65, ease: 'easeOut', delay },
    }),
};

const textStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.16, delayChildren: 0.2 } },
};

const textChild = {
    hidden: { opacity: 0, y: 22 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: 'easeOut' },
    },
};

/* ─── Main Component ───────────────────────────────────────────── */
function Admission() {
    /* Left image column */
    const { ref: leftRef, inView: leftInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* Right content column */
    const { ref: rightRef, inView: rightInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: image scale entrance */
    const imgRef = useRef(null);
    useEffect(() => {
        if (leftInView && imgRef.current) {
            gsap.fromTo(
                imgRef.current,
                { scale: 0.88, opacity: 0 },
                { scale: 1, opacity: 1, duration: 1.0, ease: 'power3.out', delay: 0.15 }
            );
        }
    }, [leftInView]);

    /* GSAP: subtle background parallax shift on scroll */
    const sectionRef = useRef(null);
    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;
        const handleScroll = () => {
            const rect = el.getBoundingClientRect();
            const progress = 1 - (rect.bottom / (window.innerHeight + rect.height));
            gsap.set(el, {
                backgroundPositionY: `calc(top + ${progress * 40}px)`,
            });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <section
            ref={sectionRef}
            className="about-area about-p pt-120 pb-120 p-relative fix"
            style={{
                backgroundImage: 'url(assets/img/bg/admission_bg.png)',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'top',
            }}
        >
            <div className="container">
                <div className="row justify-content-center align-items-center">

                    {/* ── Left: Image ── */}
                    <div className="col-lg-6 col-md-12 col-sm-12">
                        <motion.div
                            ref={leftRef}
                            className="s-about-img p-relative"
                            variants={fadeInLeft}
                            initial="hidden"
                            animate={leftInView ? 'visible' : 'hidden'}
                        >
                            <motion.img
                                ref={imgRef}
                                src={AdmissionBg}
                                alt="img"
                                style={{ opacity: 0 }}   /* GSAP reveals */
                                whileHover={{ scale: 1.03 }}
                                transition={{ duration: 0.4 }}
                            />
                        </motion.div>
                    </div>

                    {/* ── Right: Content ── */}
                    <div className="col-lg-6 col-md-12 col-sm-12">
                        <motion.div
                            ref={rightRef}
                            className="about-content s-about-content pl-15"
                            variants={fadeInRight}
                            initial="hidden"
                            animate={rightInView ? 'visible' : 'hidden'}
                        >
                            {/* Title */}
                            <motion.div
                                className="about-title second-title pb-25"
                                variants={fadeUp}
                                custom={0.05}
                                initial="hidden"
                                animate={rightInView ? 'visible' : 'hidden'}
                            >
                                <h2>Eligibility & Admissions</h2>
                            </motion.div>

                            {/* Paragraphs + button — staggered */}
                            <motion.div
                                variants={textStagger}
                                initial="hidden"
                                animate={rightInView ? 'visible' : 'hidden'}
                            >
                                <motion.p className="txt-clr" variants={textChild}>
                                    Joining Akash Institute of Pharmacy is your first step towards a
                                    rewarding career in pharmaceutical sciences. Admissions to the
                                    B.Pharm program are open to candidates who have completed 10+2
                                    with Physics and Chemistry as compulsory subjects, along with
                                    Mathematics, Biology, or both. A minimum of 40% aggregate in
                                    science subjects is required.
                                </motion.p>

                                <motion.p className="txt-clr" variants={textChild}>
                                    At AIP, we are committed to making quality pharmacy education
                                    accessible. Our admissions team is here to guide you through every
                                    step of the process — from eligibility and documentation to
                                    enrollment. Reach out to us today and take the first step towards
                                    building a meaningful career in healthcare and pharma.
                                </motion.p>

                                {/* CTA Button */}
                                <motion.div
                                    className="slider-btn mt-20"
                                    variants={textChild}
                                >
                                    <motion.div
                                        style={{ display: 'inline-block' }}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.97 }}
                                    >
                                        <Link to="/contact-us" className="btn ss-btn smoth-scroll">
                                            Read More <i className="fal fa-long-arrow-right" />
                                        </Link>
                                    </motion.div>
                                </motion.div>
                            </motion.div>

                        </motion.div>
                    </div>

                </div>
            </div>
        </section>
    );
}

export default Admission;
